import type { CashFlowEntry, CfFormula } from './cashFlowSchema';
import type { Model } from './types';

type GetFn = (key: string, t: number) => number | null;

export function evalCfFormula(
    formula: CfFormula | null | undefined,
    t: number,
    cfValues: Record<string, number | null>,
    get: GetFn,
): number | null {
    if (formula == null) {
        return null;
    }
    if (typeof formula === 'number') {
        return formula;
    }
    if (typeof formula === 'string') {
        if (formula.startsWith('cf.')) {
            return cfValues[formula] ?? null;
        }
        return get(formula, t);
    }
    const op = formula.op;
    if (op === 'delta') {
        const sign = formula.sign ?? 1;
        const curr = get(formula.key, t);
        const prev = get(formula.key, t - 1);
        if (curr == null || prev == null) {
            return null;
        }
        return sign * (curr - prev);
    }
    if (op === 'lag') {
        return get(formula.key, t - 1);
    }
    if (op === 'ref') {
        const key = formula.key;
        if (key.startsWith('cf.')) {
            return cfValues[key] ?? null;
        }
        return get(key, t);
    }
    if (op === 'const') {
        return formula.value;
    }
    if (op === 'sum') {
        let total = 0;
        for (const item of formula.of) {
            const value = evalCfFormula(item, t, cfValues, get);
            if (value != null) {
                total += value;
            }
        }
        return total;
    }
    if (op === 'sub') {
        const left = evalCfFormula(formula.a, t, cfValues, get);
        const right = evalCfFormula(formula.b, t, cfValues, get);
        if (left == null || right == null) {
            return null;
        }
        return left - right;
    }
    if (op === 'div') {
        const left = evalCfFormula(formula.a, t, cfValues, get);
        const right = evalCfFormula(formula.b, t, cfValues, get);
        if (left == null || right == null || right === 0) {
            return null;
        }
        return left / right;
    }
    throw new Error(`unknown formula op: ${String(op)}`);
}

export function evaluateCashFlowMonth(
    schema: CashFlowEntry[],
    t: number,
    get: GetFn,
): Record<string, number | null> {
    const out: Record<string, number | null> = {};
    for (const entry of schema) {
        if (entry.formula == null) {
            continue;
        }
        out[entry.key] = evalCfFormula(entry.formula, t, out, get);
    }
    return out;
}

export function applyCashFlow(model: Model, cfSchema: CashFlowEntry[]): void {
    const get: GetFn = (key, month) => {
        const value = model.values.consolidated[key]?.[month];
        return typeof value === 'number' && Number.isFinite(value) ? value : null;
    };
    for (let t = 1; t <= 36; t += 1) {
        const monthValues = evaluateCashFlowMonth(cfSchema, t, get);
        for (const [key, value] of Object.entries(monthValues)) {
            if (!model.values.consolidated[key]) {
                model.values.consolidated[key] = {};
            }
            model.values.consolidated[key]![t] = value;
        }
    }
}
